import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ShieldQuestion,
  ThumbsDown,
  ThumbsUp,
  Pencil,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../utils/supabaseStorage';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AdminPageToolbar } from '../components/admin/AdminLayout';
import { VersionDiff } from '../components/admin/VersionDiff';
import { parseAdminStaffSession } from '../utils/adminSession';
import {
  creatorNotifyDecision,
  creatorReviewAnalyze,
  superadminDecideVersion,
} from '../utils/creatorApi';
import type { ReviewAiSuggestion } from '../utils/creatorApi';
import type {
  AgentApprovalDecision,
  AgentTemplateVersionRow,
  CreatorAgentTemplateRow,
  PreflightReport,
} from '../types/creator';

interface QueueItem {
  version: AgentTemplateVersionRow;
  template: CreatorAgentTemplateRow;
  liveVersion: AgentTemplateVersionRow | null;
  creatorName: string | null;
  creatorHandle: string | null;
  creatorEmail: string | null;
}

const decisionLabel: Record<AgentApprovalDecision, string> = {
  approved: 'Approve',
  changes_requested: 'Request changes',
  rejected: 'Reject',
};

export function CreatorReviewQueuePage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [decisionInFlight, setDecisionInFlight] = useState<{
    id: string;
    decision: AgentApprovalDecision;
  } | null>(null);
  const [reasonDraft, setReasonDraft] = useState<Record<string, string>>({});
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, ReviewAiSuggestion>>({});
  const [aiInFlight, setAiInFlight] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const staff = parseAdminStaffSession();
  const isSuperadmin = staff?.role === 'superadmin';

  const load = useCallback(async () => {
    if (!isSuperadmin) return;
    setLoading(true);
    setError(null);

    const { data: pending, error: pErr } = await supabase
      .from('agent_template_versions')
      .select('*')
      .eq('version_status', 'pending')
      .order('submitted_at', { ascending: true });

    if (pErr) {
      setError(pErr.message);
      setLoading(false);
      return;
    }
    const pendingRows = (pending ?? []) as AgentTemplateVersionRow[];
    if (pendingRows.length === 0) {
      setItems([]);
      setLoading(false);
      return;
    }

    const templateIds = Array.from(new Set(pendingRows.map((v) => v.template_id)));
    const { data: templates } = await supabase
      .from('agent_templates')
      .select(
        'id, name, slug, hy_user_id, template_kind, version_status, current_version_id, paused_reason, rewards_enabled, is_active, cover_media_url, cover_media_type, public_description, updated_at, created_at'
      )
      .in('id', templateIds);
    const tplMap = new Map<string, CreatorAgentTemplateRow>();
    for (const t of (templates ?? []) as CreatorAgentTemplateRow[]) tplMap.set(t.id, t);

    const liveVersionIds = (templates ?? [])
      .map((t) => t.current_version_id)
      .filter((v): v is string => Boolean(v));
    const liveMap = new Map<string, AgentTemplateVersionRow>();
    if (liveVersionIds.length > 0) {
      const { data: liveRows } = await supabase
        .from('agent_template_versions')
        .select('*')
        .in('id', liveVersionIds);
      for (const v of (liveRows ?? []) as AgentTemplateVersionRow[]) liveMap.set(v.id, v);
    }

    const creatorIds = Array.from(
      new Set((templates ?? []).map((t) => t.hy_user_id).filter((v): v is string => Boolean(v)))
    );
    const userMap = new Map<string, { hy_name: string | null; creator_handle: string | null; email: string | null }>();
    if (creatorIds.length > 0) {
      const { data: userRows } = await supabase
        .from('users')
        .select('hy_user_id, hy_name, creator_handle, email')
        .in('hy_user_id', creatorIds);
      for (const u of (userRows ?? []) as Array<{
        hy_user_id: string;
        hy_name: string | null;
        creator_handle: string | null;
        email: string | null;
      }>) {
        userMap.set(u.hy_user_id, {
          hy_name: u.hy_name,
          creator_handle: u.creator_handle,
          email: u.email,
        });
      }
    }

    const next: QueueItem[] = pendingRows
      .map((v) => {
        const tpl = tplMap.get(v.template_id);
        if (!tpl) return null;
        const live = tpl.current_version_id ? liveMap.get(tpl.current_version_id) ?? null : null;
        const creator = tpl.hy_user_id ? userMap.get(tpl.hy_user_id) ?? null : null;
        return {
          version: v,
          template: tpl,
          liveVersion: live,
          creatorName: creator?.hy_name ?? null,
          creatorHandle: creator?.creator_handle ?? null,
          creatorEmail: creator?.email ?? null,
        };
      })
      .filter((x): x is QueueItem => Boolean(x));

    setItems(next);
    setActiveId(next[0]?.version.id ?? null);
    setLoading(false);
  }, [isSuperadmin]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = useMemo(
    () => items.find((i) => i.version.id === activeId) ?? null,
    [items, activeId]
  );

  const handleDecision = async (item: QueueItem, decision: AgentApprovalDecision) => {
    const reason = (reasonDraft[item.version.id] || '').trim();
    if ((decision === 'changes_requested' || decision === 'rejected') && !reason) {
      setError('Please write a reviewer note before requesting changes or rejecting.');
      return;
    }
    setError(null);
    setSuccess(null);
    setDecisionInFlight({ id: item.version.id, decision });

    const rpc = await superadminDecideVersion({
      versionId: item.version.id,
      decision,
      reason: reason || null,
    });
    if (!rpc.ok) {
      setError(rpc.error);
      setDecisionInFlight(null);
      return;
    }

    const notify = await creatorNotifyDecision({
      versionId: item.version.id,
      decision,
      reason: reason || null,
    });
    setDecisionInFlight(null);

    setSuccess(
      `${decisionLabel[decision]} \u2014 ${item.template.name}.${
        notify.ok && notify.delivered ? ' Creator notified.' : ' Notification skipped (no channel ready).'
      }`
    );
    setReasonDraft((prev) => {
      const next = { ...prev };
      delete next[item.version.id];
      return next;
    });
    setAiSuggestions((prev) => {
      const next = { ...prev };
      delete next[item.version.id];
      return next;
    });
    void load();
  };

  const handleAiReview = async (item: QueueItem) => {
    setAiInFlight(item.version.id);
    setAiError(null);
    const result = await creatorReviewAnalyze({ versionId: item.version.id });
    setAiInFlight(null);
    if (!result.ok) {
      setAiError(result.error);
      return;
    }
    setAiSuggestions((prev) => ({ ...prev, [item.version.id]: result.suggestion }));
    if (result.suggestion.note) {
      setReasonDraft((prev) => ({ ...prev, [item.version.id]: result.suggestion.note }));
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <p className="text-th-secondary max-w-md">
          The review queue is limited to superadmin accounts.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-base">
      <AdminPageToolbar
        title="Creator review queue"
        subtitle="Approve, request changes, or reject pending agent versions"
      >
        <button
          type="button"
          className="button-secondary text-sm inline-flex items-center gap-1.5"
          onClick={() => navigate('/admin/creators')}
        >
          <ArrowLeft className="w-4 h-4" />
          Creators
        </button>
        <button
          type="button"
          className="button-secondary text-sm inline-flex items-center gap-1.5"
          onClick={() => void load()}
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </AdminPageToolbar>

      <main className="min-h-0 flex-1 overflow-hidden max-w-6xl mx-auto w-full px-4 py-6">
        {error && (
          <div className="mb-3 p-3 rounded-lg border border-status-error/30 bg-status-error/10 text-status-error text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            {error}
          </div>
        )}
        {success && (
          <div className="mb-3 p-3 rounded-lg border border-status-success/30 bg-status-success/10 text-status-success text-sm flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5" />
            {success}
            <button type="button" className="ml-auto text-xs underline" onClick={() => setSuccess(null)}>
              Dismiss
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <div className="card p-8 text-center max-w-xl mx-auto mt-6">
            <ShieldQuestion className="w-10 h-10 text-brand mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-th-primary mb-1">Queue is clear</h2>
            <p className="text-sm text-th-secondary">
              No pending submissions. New versions show up here automatically.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 h-full min-h-0">
            <aside className="overflow-y-auto pr-1">
              <ul className="space-y-1">
                {items.map((item) => {
                  const isActive = item.version.id === activeId;
                  return (
                    <li key={item.version.id}>
                      <button
                        type="button"
                        onClick={() => setActiveId(item.version.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          isActive
                            ? 'border-brand/50 bg-brand/10'
                            : 'border-subtle bg-elevated hover:bg-overlay'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-th-primary truncate">
                            {item.template.name}
                          </p>
                          <ChevronRight className="w-4 h-4 text-th-tertiary shrink-0" />
                        </div>
                        <p className="text-xs text-th-secondary mt-0.5 truncate">
                          v{item.version.version_number} · {item.creatorName || 'unknown creator'}
                          {item.creatorHandle ? ` · @${item.creatorHandle}` : ''}
                        </p>
                        <p className="text-[10px] text-th-tertiary mt-0.5">
                          submitted{' '}
                          {item.version.submitted_at
                            ? new Date(item.version.submitted_at).toLocaleString()
                            : '—'}
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            <section className="overflow-y-auto pr-1 min-h-0">
              {active ? (
                <ReviewDetail
                  item={active}
                  reasonDraft={reasonDraft[active.version.id] || ''}
                  onReasonChange={(v) =>
                    setReasonDraft((prev) => ({ ...prev, [active.version.id]: v }))
                  }
                  onDecision={(d) => void handleDecision(active, d)}
                  inFlight={decisionInFlight?.id === active.version.id ? decisionInFlight.decision : null}
                  onOpenStudio={() =>
                    window.open(`/studio/agents/${active.template.id}/versions`, '_blank')
                  }
                  aiSuggestion={aiSuggestions[active.version.id] ?? null}
                  aiInFlight={aiInFlight === active.version.id}
                  aiError={aiError}
                  onAiReview={() => void handleAiReview(active)}
                  onDismissAiSuggestion={() =>
                    setAiSuggestions((prev) => {
                      const next = { ...prev };
                      delete next[active.version.id];
                      return next;
                    })
                  }
                />
              ) : (
                <p className="text-th-secondary">Select a submission on the left.</p>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

function ReviewDetail({
  item,
  reasonDraft,
  onReasonChange,
  onDecision,
  inFlight,
  onOpenStudio,
  aiSuggestion,
  aiInFlight,
  aiError,
  onAiReview,
  onDismissAiSuggestion,
}: {
  item: QueueItem;
  reasonDraft: string;
  onReasonChange: (v: string) => void;
  onDecision: (d: AgentApprovalDecision) => void;
  inFlight: AgentApprovalDecision | null;
  onOpenStudio: () => void;
  aiSuggestion: ReviewAiSuggestion | null;
  aiInFlight: boolean;
  aiError: string | null;
  onAiReview: () => void;
  onDismissAiSuggestion: () => void;
}) {
  const payload = (item.version.payload || {}) as Record<string, unknown>;
  const livePayload = (item.liveVersion?.payload || {}) as Record<string, unknown>;
  const preflight = item.version.preflight_report as PreflightReport | null;
  const changelog = typeof payload.changelog === 'string' ? payload.changelog : '';

  return (
    <div className="space-y-4">
      <header className="card p-4 space-y-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-th-primary tracking-tight">{item.template.name}</h2>
            <p className="text-xs text-th-secondary">
              v{item.version.version_number} · creator{' '}
              <strong>{item.creatorName || '(unknown)'}</strong>
              {item.creatorHandle ? (
                <>
                  {' '}
                  · <span className="font-mono">@{item.creatorHandle}</span>
                </>
              ) : null}
              {item.creatorEmail ? ` · ${item.creatorEmail}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onOpenStudio}
            className="button-secondary text-sm inline-flex items-center gap-1.5"
          >
            <Pencil className="w-4 h-4" />
            Open in studio
          </button>
        </div>
        {changelog && (
          <p className="text-sm text-th-secondary whitespace-pre-wrap">
            <span className="text-th-primary font-semibold">Creator changelog:</span> {changelog}
          </p>
        )}
      </header>

      {preflight && (
        <section className="card p-4 space-y-2">
          <h3 className="text-sm font-semibold text-th-primary">Automated preflight</h3>
          <p className="text-xs text-th-secondary">
            Ran {new Date(preflight.checked_at).toLocaleString()} · checks: {preflight.checks_run.join(', ')}
            {preflight.moderation.ran
              ? ` · moderation ${preflight.moderation.flagged ? 'flagged' : 'clean'}`
              : ''}
          </p>
          {preflight.hard_failures.length === 0 && preflight.soft_warnings.length === 0 ? (
            <p className="text-xs text-status-success inline-flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> No issues found.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {[...preflight.hard_failures, ...preflight.soft_warnings].map((issue, idx) => (
                <li
                  key={`${issue.code}-${idx}`}
                  className={`text-xs flex items-start gap-2 px-2 py-1.5 rounded border ${
                    issue.severity === 'hard'
                      ? 'border-status-error/30 bg-status-error/10 text-status-error'
                      : 'border-status-warning/30 bg-status-warning/10 text-status-warning'
                  }`}
                >
                  <span className="font-mono shrink-0">
                    {issue.severity === 'hard' ? 'BLOCK' : 'WARN'}
                  </span>
                  <span className="min-w-0 break-words">
                    <strong className="font-semibold">{issue.code}</strong>: {issue.message}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="card p-4 space-y-2">
        <h3 className="text-sm font-semibold text-th-primary">
          Diff vs {item.liveVersion ? `live v${item.liveVersion.version_number}` : 'no live version'}
        </h3>
        <VersionDiff
          before={item.liveVersion ? livePayload : null}
          after={payload}
          beforeLabel={item.liveVersion ? `Live v${item.liveVersion.version_number}` : 'No live'}
          afterLabel={`Pending v${item.version.version_number}`}
        />
      </section>

      <section className="card p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-th-primary">Decision</h3>
          <button
            type="button"
            onClick={onAiReview}
            disabled={aiInFlight}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-brand/40 bg-brand/10 text-brand hover:bg-brand/20 disabled:opacity-50 transition-colors"
            title="Let AI analyse the submission and draft a reviewer note"
          >
            {aiInFlight ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {aiSuggestion ? 'Re-run AI review' : 'AI review'}
          </button>
        </div>

        {aiError && (
          <div className="p-2.5 rounded-lg border border-status-error/30 bg-status-error/10 text-status-error text-xs flex items-start gap-2">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{aiError}</span>
          </div>
        )}

        {aiSuggestion && <AiSuggestionCard suggestion={aiSuggestion} onDismiss={onDismissAiSuggestion} />}

        <label className="block space-y-1">
          <span className="text-xs text-th-secondary">
            Reviewer note (required for changes / reject)
          </span>
          <textarea
            value={reasonDraft}
            onChange={(e) => onReasonChange(e.target.value)}
            rows={3}
            className="w-full bg-elevated border border-subtle rounded-lg px-3 py-2 text-th-primary resize-y min-h-[80px]"
            placeholder="What does the creator need to change? Be specific. Click AI review to draft a suggestion."
          />
        </label>
        <div className="flex flex-wrap gap-2">
          <DecisionButton
            decision="approved"
            label="Approve & publish"
            icon={ThumbsUp}
            tone="success"
            inFlight={inFlight === 'approved'}
            onClick={() => onDecision('approved')}
          />
          <DecisionButton
            decision="changes_requested"
            label="Request changes"
            icon={Pencil}
            tone="warning"
            inFlight={inFlight === 'changes_requested'}
            onClick={() => onDecision('changes_requested')}
          />
          <DecisionButton
            decision="rejected"
            label="Reject"
            icon={ThumbsDown}
            tone="error"
            inFlight={inFlight === 'rejected'}
            onClick={() => onDecision('rejected')}
          />
        </div>
      </section>
    </div>
  );
}

function AiSuggestionCard({
  suggestion,
  onDismiss,
}: {
  suggestion: ReviewAiSuggestion;
  onDismiss: () => void;
}) {
  const toneCls =
    suggestion.recommendation === 'approved'
      ? 'border-status-success/40 bg-status-success/10'
      : suggestion.recommendation === 'rejected'
        ? 'border-status-error/40 bg-status-error/10'
        : 'border-status-warning/40 bg-status-warning/10';
  const toneText =
    suggestion.recommendation === 'approved'
      ? 'text-status-success'
      : suggestion.recommendation === 'rejected'
        ? 'text-status-error'
        : 'text-status-warning';
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${toneCls}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className={`w-4 h-4 shrink-0 ${toneText}`} />
          <span className="text-xs font-semibold text-th-primary">
            AI suggests:{' '}
            <span className={`uppercase tracking-wide ${toneText}`}>
              {decisionLabel[suggestion.recommendation]}
            </span>
            <span className="text-th-tertiary font-normal ml-1">({suggestion.confidence} confidence)</span>
          </span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-[11px] text-th-tertiary hover:text-th-primary underline"
        >
          Dismiss
        </button>
      </div>

      {suggestion.summary && (
        <p className="text-xs text-th-secondary whitespace-pre-wrap">{suggestion.summary}</p>
      )}

      {suggestion.highlights.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-th-primary mb-1">Highlights</p>
          <ul className="text-xs text-th-secondary space-y-0.5 list-disc pl-4">
            {suggestion.highlights.map((h, i) => (
              <li key={`h-${i}`}>{h}</li>
            ))}
          </ul>
        </div>
      )}

      {suggestion.concerns.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-th-primary mb-1">Concerns</p>
          <ul className="text-xs text-th-secondary space-y-0.5 list-disc pl-4">
            {suggestion.concerns.map((c, i) => (
              <li key={`c-${i}`}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      {(suggestion.knowledge_base_review.assessment ||
        suggestion.knowledge_base_review.issues.length > 0) && (
        <div className="rounded-md border border-subtle bg-base/40 p-2">
          <p className="text-[11px] font-semibold text-th-primary mb-1">
            Knowledge base review{' '}
            <span className="text-th-tertiary font-normal">
              ({suggestion.knowledge_base_review.present ? 'present' : 'empty / disabled'})
            </span>
          </p>
          {suggestion.knowledge_base_review.assessment && (
            <p className="text-xs text-th-secondary whitespace-pre-wrap">
              {suggestion.knowledge_base_review.assessment}
            </p>
          )}
          {suggestion.knowledge_base_review.issues.length > 0 && (
            <ul className="text-xs text-th-secondary space-y-0.5 list-disc pl-4 mt-1">
              {suggestion.knowledge_base_review.issues.map((issue, i) => (
                <li key={`kb-${i}`}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <p className="text-[11px] text-th-tertiary">
        The drafted note has been loaded into the reviewer-note textarea — edit it before deciding.
      </p>
    </div>
  );
}

function DecisionButton({
  label,
  icon: Icon,
  tone,
  inFlight,
  onClick,
}: {
  decision: AgentApprovalDecision;
  label: string;
  icon: typeof ThumbsUp;
  tone: 'success' | 'warning' | 'error';
  inFlight: boolean;
  onClick: () => void;
}) {
  const cls =
    tone === 'success'
      ? 'bg-status-success/15 border-status-success/40 text-status-success hover:bg-status-success/25'
      : tone === 'warning'
        ? 'bg-status-warning/15 border-status-warning/40 text-status-warning hover:bg-status-warning/25'
        : 'bg-status-error/15 border-status-error/40 text-status-error hover:bg-status-error/25';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={inFlight}
      className={`px-3 py-2 text-sm rounded-lg border inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors ${cls}`}
    >
      {inFlight ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      {label}
    </button>
  );
}
